/**
 * Terminal theme — CSS variable overrides.
 *
 * Terminal is force-dark: the light/dark toggle is a no-op while Terminal
 * is active, so the same override map is applied in both `light` and `dark`
 * slots. If we ever want a true "terminal-light" variant, it'd be a separate
 * built-in theme.
 *
 * The companion SCSS lives next to this file in global.scss (scoped to
 * [data-app-theme="terminal"]) and chrome.scss (for the replacement
 * header/aside components).
 */

import type {AppThemeDoc, ThemeOverrides} from '../../services/themeCustomizerService'

export type BuiltInTheme = AppThemeDoc & {
  id: string
  slug: string
  isBuiltIn: true
}

const OVERRIDES: ThemeOverrides = {
  // ── Bootstrap 5 core tokens ────────────────────────────────────────────
  '--bs-body-bg':             '#0a0a0a',
  '--bs-body-color':          '#d4d4d4',
  '--bs-emphasis-color':      '#ffffff',
  '--bs-secondary-color':     '#8b8b8b',
  '--bs-tertiary-color':      '#5a5a5a',
  '--bs-secondary-bg':        '#111113',
  '--bs-tertiary-bg':         '#141417',
  '--bs-body-bg-rgb':         '10, 10, 10',
  '--bs-body-color-rgb':      '212, 212, 212',

  '--bs-border-color':        '#262626',
  '--bs-border-color-translucent': 'rgba(255, 255, 255, 0.08)',
  '--bs-border-radius':       '0',
  '--bs-border-radius-sm':    '0',
  '--bs-border-radius-lg':    '0',
  '--bs-border-radius-xl':    '0',
  '--bs-border-radius-xxl':   '0',
  '--bs-border-radius-pill':  '0',

  // Accent / semantic colors — terminal phosphor-green + status tones
  '--bs-primary':             '#4ade80',
  '--bs-primary-rgb':         '74, 222, 128',
  '--bs-primary-bg-subtle':   'rgba(74, 222, 128, 0.08)',
  '--bs-primary-border-subtle':'rgba(74, 222, 128, 0.3)',
  '--bs-primary-text-emphasis':'#4ade80',

  '--bs-secondary':           '#6b6b6b',
  '--bs-secondary-rgb':       '107, 107, 107',

  '--bs-success':             '#22c55e',
  '--bs-success-rgb':         '34, 197, 94',
  '--bs-success-bg-subtle':   'rgba(34, 197, 94, 0.08)',
  '--bs-success-border-subtle':'rgba(34, 197, 94, 0.3)',

  '--bs-danger':              '#ef4444',
  '--bs-danger-rgb':          '239, 68, 68',
  '--bs-danger-bg-subtle':    'rgba(239, 68, 68, 0.08)',
  '--bs-danger-border-subtle':'rgba(239, 68, 68, 0.3)',

  '--bs-warning':             '#fbbf24',
  '--bs-warning-rgb':         '251, 191, 36',
  '--bs-warning-bg-subtle':   'rgba(251, 191, 36, 0.08)',
  '--bs-warning-border-subtle':'rgba(251, 191, 36, 0.3)',

  '--bs-info':                '#60a5fa',
  '--bs-info-rgb':            '96, 165, 250',
  '--bs-info-bg-subtle':      'rgba(96, 165, 250, 0.08)',
  '--bs-info-border-subtle':  'rgba(96, 165, 250, 0.3)',

  '--bs-light':               '#141417',
  '--bs-light-rgb':           '20, 20, 23',
  '--bs-dark':                '#d4d4d4',
  '--bs-dark-rgb':            '212, 212, 212',

  '--bs-link-color':          '#4ade80',
  '--bs-link-color-rgb':      '74, 222, 128',
  '--bs-link-hover-color':    '#22c55e',
  '--bs-link-hover-color-rgb':'34, 197, 94',

  // ── Metronic app-level tokens ──────────────────────────────────────────
  '--kt-page-bg':                            '#0a0a0a',
  '--kt-app-bg-color':                       '#0a0a0a',
  '--kt-app-header-base-bg-color':           '#0a0a0a',
  '--kt-app-header-sticky-bg-color':         '#0a0a0a',
  '--kt-app-header-base-border-color':       '#1a1a1a',
  '--kt-app-sidebar-base-bg-color':          '#0a0a0a',
  '--kt-app-sidebar-base-border-color':      '#1a1a1a',
  '--kt-app-aside-base-bg-color':            '#0a0a0a',
  '--kt-app-aside-base-border-color':        '#1a1a1a',
  '--kt-app-footer-bg-color':                '#0a0a0a',

  '--kt-card-bg':              '#111113',
  '--kt-card-border-color':    '#262626',
  '--kt-card-box-shadow':      'none',

  '--kt-text-dark':            '#ffffff',
  '--kt-text-gray-900':        '#f5f5f5',
  '--kt-text-gray-800':        '#d4d4d4',
  '--kt-text-gray-700':        '#a8a8a8',
  '--kt-text-gray-600':        '#8b8b8b',
  '--kt-text-gray-500':        '#6b6b6b',
  '--kt-text-gray-400':        '#5a5a5a',
  '--kt-text-gray-300':        '#3d3d3d',
  '--kt-text-gray-200':        '#262626',
  '--kt-text-gray-100':        '#1a1a1a',

  '--kt-gray-900':             '#f5f5f5',
  '--kt-gray-800':             '#d4d4d4',
  '--kt-gray-700':             '#a8a8a8',
  '--kt-gray-600':             '#8b8b8b',
  '--kt-gray-500':             '#6b6b6b',
  '--kt-gray-400':             '#5a5a5a',
  '--kt-gray-300':             '#3d3d3d',
  '--kt-gray-200':             '#262626',
  '--kt-gray-100':             '#1a1a1a',

  '--kt-primary':              '#4ade80',
  '--kt-primary-active':       '#22c55e',
  '--kt-primary-light':        'rgba(74, 222, 128, 0.1)',
  '--kt-primary-inverse':      '#0a0a0a',

  '--kt-border-color':         '#262626',
  '--kt-border-dashed-color':  '#262626',

  '--kt-input-bg':             '#141417',
  '--kt-input-solid-bg':       '#141417',
  '--kt-input-color':          '#d4d4d4',
  '--kt-input-border-color':   '#262626',
  '--kt-input-placeholder-color':'#5a5a5a',

  '--kt-menu-link-color-hover':  '#4ade80',
  '--kt-menu-link-color-active': '#4ade80',
  '--kt-menu-link-bg-hover':     'rgba(74, 222, 128, 0.06)',
  '--kt-menu-link-bg-active':    'rgba(74, 222, 128, 0.08)',

  '--kt-tooltip-bg':           '#141417',
  '--kt-tooltip-color':        '#d4d4d4',

  '--kt-modal-bg':             '#111113',
  '--kt-modal-header-border-color': '#262626',
  '--kt-modal-footer-border-color': '#262626',

  '--kt-dropdown-bg':          '#141417',
  '--kt-dropdown-border-color':'#262626',
  '--kt-dropdown-link-color':  '#d4d4d4',
  '--kt-dropdown-link-bg-hover':'rgba(74, 222, 128, 0.06)',
}

export const TERMINAL_THEME: BuiltInTheme = {
  id: '__builtin_terminal',
  slug: 'terminal',
  name: 'Terminal',
  description: 'Retro command-line. Monospace, phosphor green, sharp edges.',
  isBuiltIn: true,
  overrides: {
    light: OVERRIDES,
    dark:  OVERRIDES,
  },
}
